/* DNOVA homepage behaviour. Every feature degrades to static, readable content. */
(() => {
  if (window.__dnovaHome) return;
  window.__dnovaHome = true;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Entrance: only elements still below the fold are held back, so nothing
     that is already on screen ever blinks out. */
  const initReveal = (root) => {
    const targets = root.querySelectorAll('[data-dn-reveal]');
    if (reduceMotion || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.remove('is-pending');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    targets.forEach((el) => {
      if (el.getBoundingClientRect().top > window.innerHeight) {
        el.classList.add('is-pending');
        observer.observe(el);
      }
    });
  };

  const initFlow = (root) => {
    root.querySelectorAll('.dn-flow').forEach((flow) => {
      if (reduceMotion || !('IntersectionObserver' in window)) { flow.classList.add('is-flowing'); return; }
      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) { flow.classList.add('is-flowing'); observer.disconnect(); }
      }, { threshold: 0.15 });
      observer.observe(flow);
    });
  };

  const initShelf = (root) => {
    root.querySelectorAll('[data-dn-shelf]').forEach((shelf) => {
      const rail = shelf.querySelector('.dn-shelf__rail');
      const prev = shelf.querySelector('[data-dn-prev]');
      const next = shelf.querySelector('[data-dn-next]');
      if (!rail || !prev || !next) return;
      const step = () => Math.max(rail.clientWidth * 0.8, 240);
      const update = () => {
        prev.disabled = rail.scrollLeft < 8;
        next.disabled = rail.scrollLeft + rail.clientWidth > rail.scrollWidth - 8;
      };
      prev.addEventListener('click', () => rail.scrollBy({ left: -step() }));
      next.addEventListener('click', () => rail.scrollBy({ left: step() }));
      rail.addEventListener('scroll', update, { passive: true });
      update();
    });
  };

  const initVoices = (root) => {
    root.querySelectorAll('[data-dn-voices]').forEach((wrap) => {
      const voices = [...wrap.querySelectorAll('.dn-voice')];
      const dots = [...wrap.querySelectorAll('.dn-voices__dots button')];
      if (voices.length < 2) { voices[0]?.classList.add('is-active'); return; }
      let index = 0;
      let timer;
      const show = (i) => {
        index = (i + voices.length) % voices.length;
        voices.forEach((v, n) => {
          v.classList.toggle('is-active', n === index);
          v.setAttribute('aria-hidden', n === index ? 'false' : 'true');
        });
        dots.forEach((d, n) => d.setAttribute('aria-current', n === index ? 'true' : 'false'));
      };
      const play = () => {
        clearInterval(timer);
        if (!reduceMotion) timer = setInterval(() => show(index + 1), 7000);
      };
      dots.forEach((d, n) => d.addEventListener('click', () => { show(n); play(); }));
      wrap.addEventListener('mouseenter', () => clearInterval(timer));
      wrap.addEventListener('mouseleave', play);
      wrap.addEventListener('focusin', () => clearInterval(timer));
      show(0);
      play();
    });
  };

  /* The configurator assembles a readable spec and a short code from the chosen
     options, then carries both into the quote request. */
  const initConfigurator = (root) => {
    root.querySelectorAll('[data-dn-config]').forEach((form) => {
      const output = form.querySelector('[data-dn-code]');
      const summary = form.querySelector('[data-dn-summary]');
      const mail = form.querySelector('[data-dn-mail]');
      const quote = form.querySelector('[data-dn-quote]');
      const family = form.dataset.family || 'Valve';
      const email = form.dataset.email;
      const render = () => {
        const groups = [...form.querySelectorAll('.dn-config__group')];
        const picks = groups.map((g) => {
          const input = g.querySelector('input:checked');
          return input ? { label: g.dataset.label, value: input.value, code: input.dataset.code } : null;
        }).filter(Boolean);
        const code = [form.dataset.prefix, ...picks.map((p) => p.code)].filter(Boolean).join('-');
        if (output && output.textContent !== code) {
          output.classList.add('is-changing');
          setTimeout(() => { output.textContent = code; output.classList.remove('is-changing'); }, reduceMotion ? 0 : 180);
        }
        if (summary) {
          summary.innerHTML = '';
          picks.forEach((p) => {
            const dt = document.createElement('dt'); dt.textContent = p.label;
            const dd = document.createElement('dd'); dd.textContent = p.value;
            summary.append(dt, dd);
          });
        }
        const lines = picks.map((p) => `${p.label}: ${p.value}`).join('\n');
        if (mail && email) {
          const subject = `Quote request: ${family} ${code}`;
          const body = `Hello DNOVA,\n\nPlease quote this configuration:\n\n${family}\n${lines}\n\nQuantity:\nSize (DN / inch):\nDelivery country:\n\nThank you.`;
          mail.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        }
        if (quote) {
          const url = new URL(quote.getAttribute('href'), window.location.origin);
          url.searchParams.set('config', `${family} ${code}`);
          quote.href = url.pathname + url.search;
        }
      };
      form.addEventListener('change', render);
      form.addEventListener('submit', (e) => e.preventDefault());
      render();
    });
  };

  const init = (root = document) => {
    initReveal(root);
    initFlow(root);
    initShelf(root);
    initVoices(root);
    initConfigurator(root);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();

/* 2026-09-29: changing hero word, alternating lineup rows, background video */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* The changing word: out, then in, one element, so two words never share the box. The box
     width follows the new word. Runs only while the hero is on screen and the tab is visible. */
  const initRotator = (root) => {
    root.querySelectorAll('.dn-rot:not([data-ready])').forEach((rot) => {
      const words = (rot.dataset.words || '').split('|').map((w) => w.trim()).filter(Boolean);
      const el = rot.querySelector('.dn-rot__w');
      if (words.length < 2 || !el || reduceMotion) return;
      rot.dataset.ready = '1';
      let index = 0, visible = true, busy = false;
      const fit = () => { rot.style.width = el.offsetWidth + 'px'; };
      const swap = () => {
        if (busy || !visible || document.hidden) return;
        busy = true; index = (index + 1) % words.length;
        el.classList.add('is-out');
        setTimeout(() => {
          el.textContent = words[index] + '.';
          el.classList.remove('is-out'); el.classList.add('is-pre');
          fit(); void el.offsetWidth;
          el.classList.remove('is-pre');
          setTimeout(() => { busy = false; }, 420);
        }, 360);
      };
      (document.fonts && document.fonts.ready || Promise.resolve()).then(() => { fit(); setTimeout(() => setInterval(swap, 2800), 1800); });
      window.addEventListener('resize', () => { if (!busy) fit(); });
      if ('IntersectionObserver' in window) new IntersectionObserver(([en]) => { visible = en.isIntersecting; }).observe(rot);
    });
  };

  /* Lineup: row 1 slides in from the left, row 2 from the right, and so on down the grid. */
  const initLineup = (root) => {
    root.querySelectorAll('.dn-lineup__rail').forEach((rail) => {
      if (reduceMotion || !('IntersectionObserver' in window) || rail.dataset.slid) return;
      rail.dataset.slid = '1';
      const cards = [...rail.querySelectorAll('.dn-family')];
      const cols = Math.max(1, getComputedStyle(rail).gridTemplateColumns.split(' ').length);
      const oneRow = rail.scrollWidth > rail.clientWidth + 4 || cols >= cards.length;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          en.target.classList.remove('is-pending'); io.unobserve(en.target);
          setTimeout(() => en.target.removeAttribute('data-dn-slide'), 1500);
        });
      }, { rootMargin: '0px 0px -10% 0px' });
      cards.forEach((card, i) => {
        const row = oneRow ? 0 : Math.floor(i / cols), col = oneRow ? i : i % cols;
        const fromLeft = row % 2 === 0;
                card.style.setProperty('--dn-d', ((oneRow ? Math.min(col, 3) : col + row * 0.6) * 0.1) + 's');
        if (card.getBoundingClientRect().top > window.innerHeight) { card.setAttribute('data-dn-slide', ''); card.classList.add('is-pending'); io.observe(card); }
      });
    });
  };

  /* The plant video only loads and plays while its section is on screen. */
  const initVideo = (root) => {
    root.querySelectorAll('video[data-dn-bgvideo]').forEach((video) => {
      if (reduceMotion || (navigator.connection && navigator.connection.saveData) || video.dataset.armed) return;
      video.dataset.armed = '1';
      new IntersectionObserver(([en]) => {
        if (en.isIntersecting) {
          if (!video.src) video.src = video.dataset.src;
          video.play().catch(() => {});
        } else video.pause();
      }, { rootMargin: '200px 0px' }).observe(video);
    });
  };

  const init = (root = document) => { initRotator(root); initLineup(root); initVideo(root); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
